'use client'

import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Flex } from '@chakra-ui/react'
import { useFormik } from 'formik'
import { useRouter } from 'next/navigation'
import * as Yup from 'yup'

import { Input } from 'components/form/Input'
import { Select } from 'components/form/Select'
import { Button } from 'components/ui/Buttons/Button'
import { selectLoadingState, selectSuccessState } from 'store/partials/contact/contactSelectors'
import { resetSuccessState, sendContactForm } from 'store/partials/contact/contactSlice'
import { REASONS_FOR_CONTACT } from 'utils/constants'

export const ContactForm = (): JSX.Element => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const dispatch = useDispatch()

  const isLoading = useSelector(selectLoadingState)
  const isSuccess = useSelector(selectSuccessState)

  const { getFieldProps, touched, errors, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      body: '',
      reason: '',
    },
    onSubmit: (values) => {
      dispatch(sendContactForm(values))
    },
    validationSchema: Yup.object().shape({
      fullName: Yup.string().required(t('required')),
      email: Yup.string().email(t('invalidEmail')).required(t('required')),
      body: Yup.string().required(t('required')),
    }),
  })

  useEffect(() => {
    if (isSuccess) {
      router.push('/contact/success')
      dispatch(resetSuccessState())
    }
  }, [isSuccess])

  return (
    <Flex direction={['column', 'row']} marginTop="80px" justifyContent="space-between">
      <Box width={['100%', '40%']}>
        <Input
          {...getFieldProps('fullName')}
          placeholder={t('fullName')}
          name="fullName"
          touched={touched.fullName}
          error={errors.fullName}
          label={t('fullName')}
        />
        <Box marginTop="20px">
          <Input
            {...getFieldProps('email')}
            placeholder={t('email')}
            name="email"
            touched={touched.email}
            error={errors.email}
            label={t('email')}
          />
        </Box>
        <Box marginTop="20px">
          <Select
            name="reason"
            placeholder={t('reasonForContacting')}
            options={REASONS_FOR_CONTACT}
            onChange={(val) => setFieldValue('reason', t(val))}
            className="gtm-reason-for-contact"
          />
        </Box>
      </Box>
      <Box width={['100%', '50%']} marginTop={['20px', 0]}>
        <Input
          {...getFieldProps('body')}
          placeholder={t('message')}
          name="body"
          touched={touched.body}
          error={errors.body}
          type="textarea"
          label={t('message')}
        />
        <Flex
          alignItems={['flex-start', 'center']}
          justifyContent="flex-end"
          marginTop="30px"
          direction={['column', 'row']}
        >
          <Box width={{ base: '100%', md: '185px' }} marginTop={{ base: '52px', md: 0 }}>
            <Button isDisabled={isLoading} type="submit" onClick={handleSubmit} className="gtm-contact-us-send-btn">
              {t('submit')}
            </Button>
          </Box>
        </Flex>
      </Box>
    </Flex>
  )
}
